import { Button, Chip, Grid, TextField } from "@mui/material"
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { AlertView } from "./alert";
import { AxiosError } from "axios";
import { getApiAccessToken } from "../auth/auth";
import { addGroupUsers } from "../api/groups";
import { Group } from "../model/group";
import { addUsers, getUsers } from "../api/users";
import { Loader } from "./loader";

export interface UserInputProps {
    onAdded: () => void
    group: Group | null
}

export const UserInputs: React.FC<UserInputProps> = (props: UserInputProps) => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    const [errorMessage, setError] = useState<string | null>(null);

    const [emails, setEmails] = useState<string[]>([]);
    const [canSubmit, setCanSubmit] = useState<boolean>(true);

    const [loading, setLoading] = useState<boolean>(false);

    const processEmailInputs = (input: string) => {
        // Regular expression pattern to match email addresses
        const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
      
        // Split the input based on newlines or commas
        const potentialEmails = input.split(/[\n, ]/);
        
        // Filter out valid email addresses using the regular expression
        const emails = potentialEmails.filter(item => item.match(emailPattern));
        setEmails(emails)
    }

    const handleSubmitUsers = async (emails: string[]) => {
        try {
            if (props.group == null) {
                console.log("unexpected group is null")
                return
            }

            let accessToken = await getApiAccessToken(instance, activeAccount)

            // first step is to add users that might not be in the system yet
            let addedUsers = await addUsers(accessToken, emails);
            let addedUserIds = addedUsers.map(user => user.id)

            // next we add all those new users to the target group
            await addGroupUsers(accessToken, props.group.id, addedUserIds);

            // notify parent to rerender users
            props.onAdded()
            setEmails([])
            setCanSubmit(true)
        } catch (error) {
            if (error instanceof AxiosError) {
                let code = Number(error?.status)

                if (code >= 400 && code < 500) {
                    setError(error?.message)
                } else {
                    setError(error.message)
                }

            } else if (error instanceof Error) {
                console.error(error);
            }
            setCanSubmit(true)
        }
    }

    // indicates button has been clicked, but cancel after a period of time
    const handleCsvClick = () => {
        setLoading(true);
        setTimeout(() => {
            setLoading(false)
        }, 2000);
    }

    const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target?.result as string;
                processEmailInputs(text);
            };
            reader.readAsText(file);
            event.target.value = '';
        }

        setLoading(false); 
    }

    return(
        <Grid container direction="column" sx={{ marginBottom: 5 }}  alignItems="left" justifyContent="left" textAlign="left">
            <AlertView error={errorMessage}/>
            <Grid container direction="row" spacing={2} sx={{ marginBottom: 2, marginTop: 1 }}>
                <Grid item>
                    <TextField
                        id="outlined-multiline-flexible"
                        label="Add user emails"
                        sx={{ maxWidth: 650, minWidth: 400 }}
                        multiline
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            processEmailInputs(event.target.value)
                        }}
                    />
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        component="label"
                        sx={{ maxWidth: 200, height: '56px' }}
                    >
                        {loading ? <Loader width={20} height={20}/>: 
                        "Upload .CSV"}
                        <input
                            type="file"
                            accept=".csv"
                            hidden
                            onClick={handleCsvClick}
                            onChange={handleCsvUpload}
                        />
                    </Button>
                </Grid>
            </Grid>
            <Grid container direction="row" sx={{ marginBottom: 2 }}>
                {
                    emails.map(email => {
                        return(
                            <Chip
                            key={email}
                            label={email}
                            variant="outlined" 
                            sx={{ maxWidth: 300, flexShrink: 1, margin: 1 }}
                        />
                        )
                    })
                }
            </Grid>
            {
                emails.length > 0 ? 
                    <Button 
                        variant="outlined"
                        disabled={!canSubmit}
                        onClick={() => {
                            handleSubmitUsers(emails)
                            setCanSubmit(false)
                        }}
                        sx={{ maxWidth: 200, marginBottom: 2 }}>
                            Submit
                    </Button> : 
                    <></>
            }
        </Grid>
    )
}